import { LoaderUserActionPreventionPredicate } from '#/CoreFrameInterfaces';
import EventHelper from '@/Utility/EventHelper';
import Utilities from '@/Utility/Utilities';
import * as LoggingManager from '@/Utility/LoggingManager';
import { singleton } from "tsyringe";
import { ILoader } from "@/Utility/Loader";

export const DEFAULT_LOADER_HIDE_DELAY_MS = 200;

@singleton()
export class LoaderImpl implements ILoader {

    private LOADER_CLASS = 'c-loader' as const;
    private LOADER_DATA_ID = 'loader-id' as const;
    private LOADER_DATA_ID_ATTR = `data-${this.LOADER_DATA_ID}` as const;

    private counter: { [id: number]: number } = {};
    private hideTimers: { [id: number]: number } = {};

    private $overlay = $('<div class="overlay"></div>');
    private $loader = $('<div class="loader"><div class="loader-inner"><div class="loader-animation"><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div><div class="loader-inner-bar"></div></div></div></div>');
    private $tabbables: JQuery[] = [];
    private activeElement: Element = null;
    private userActionsPreventionExceptions: LoaderUserActionPreventionPredicate[] = [];
    private userActionPreventionActive: boolean = false;
    private pendingLoaderElements: HTMLElement[] = [];

    private mutationObserverTimeout: number = 60_000;
    private globalLoaderId = -1;

    init() {
        $(window).on('keypress', this.preventUserActions.bind(this));
        $(window).on('click', this.preventUserActions.bind(this));
    }
    
    private on($element: JQuery, showLoader?: boolean, showOverlay?: boolean) {
        if (this.isBodyElement($element) && this.isGlobalLoaderActive())  {
            this.increaseCounter(this.globalLoaderId);
            return;
        }

        const htmlElement = $element[0];
        const isConnected = htmlElement.isConnected;

        if (isConnected) {
            this.appendLoader($element, showLoader, showOverlay, true);
            return;
        }

        this.waitForDomAttach(htmlElement).then((isAttached) => {
            if (!isAttached)
                return;
            this.appendLoader($element, showLoader, showOverlay, false);
        });
    }

    private appendLoader($element: JQuery, showLoader: boolean, showOverlay: boolean, elementWasAlreadyConnected: boolean) {
        if (!elementWasAlreadyConnected && !this.pendingLoaderElements.includes($element[0])) {
            return;
        }

        this.removePendingElement($element[0]);

        const loaderId = this.getId($element) ?? this.generateId();
        this.resetTimer(loaderId);

        const count = this.increaseCounter(loaderId);
        if (count !== 1)
            return;

        $element.addClass(this.LOADER_CLASS);
        $element.attr(this.LOADER_DATA_ID_ATTR, loaderId);

        if ($element.css('position') != 'absolute') { // position: absolute darf nicht einfach auf relative geändert werden!
            $element.css('position', 'relative');
        }
        if (showOverlay !== false && $element.find('>.overlay').length === 0) {
            this.$overlay.clone().attr(this.LOADER_DATA_ID_ATTR, loaderId).appendTo($element);
        }
        if (showLoader !== false && $element.find('>.loader').length === 0) {
            this.$loader.clone().attr(this.LOADER_DATA_ID_ATTR, loaderId).appendTo($element);
        }
        if ($element.parent().is('html')) {
            this.addUserActionPrevention();
        }
    }

    private async waitForDomAttach(element: HTMLElement): Promise<boolean> {
        if (Utilities.isNullOrUndefined(element))
            return Promise.resolve(false);

        this.pendingLoaderElements.push(element);
        return new Promise((resolve) => {
            const observerTimeoutId = window.setTimeout(() => {
                observer?.disconnect();
                resolve(false);
            }, this.mutationObserverTimeout);

            const observer = new MutationObserver(() => {
                const isNodeConnected = element.isConnected;
                if (isNodeConnected) {
                    observer.disconnect();
                    resolve(true);
                    window.clearTimeout(observerTimeoutId);
                }
            });

            observer.observe(document, {childList: true, subtree: true});
        });
    }

    private isGlobalLoaderActive(): boolean {
        const $body = $('body')
        return $body.children('.loader').length > 0
            && $body.children('.overlay').length > 0;
    }

    private addUserActionPrevention() {
        this.disableTabbables();

        this.userActionPreventionActive = true;
    }

    private removeUserActionPrevention() {
        this.restoreTabbables();

        this.userActionPreventionActive = false;
    }

    private disableTabbables() {
        this.activeElement = document.activeElement;
        $(':tabbable').each((i, e) => {
            let $element = $(e);

            this.$tabbables.push($element);

            $element['tabindexToRestore'] = $element.attr('tabindex');
            $element.attr('tabindex', -1);
        });
    }

    private restoreTabbables() {
        this.$tabbables.forEach(($element) => {
            if ($element['tabindexToRestore'] !== undefined) {
                $element.attr('tabindex', $element['tabindexToRestore']);
            } else {
                $element.removeAttr('tabindex');
            }
        });

        if (this.activeElement != null && this.activeElement instanceof HTMLElement)
            this.activeElement.focus();
        this.activeElement = null;
        this.$tabbables = [];
    }

    private preventUserActions(e: JQuery.ClickEvent | JQuery.KeyPressEvent) {
        if (!this.userActionPreventionActive)
            return;

        if (EventHelper.isIgnoredByRADEventHandlers(e.target))
            return;

        if (_.any(this.userActionsPreventionExceptions, predicate => !predicate(e)))
            return;

        e.preventDefault();
        e.stopPropagation();

        if (window.testModeEnabled) {
            const $element = $(e.target);
            const message = `User action prevented on ${e.target.toString()} (id="${$element.attr('id')}", class="${$element.attr('class')}", data-locator="${$element.data('locator')}")`;
            LoggingManager.logMessage(LoggingManager.LogLevel.Debug, message, document.location.href);
        }
    }

    /**
     * Registriert ein Predicate, welches entscheidet, ob eine UserAction während eines Loaders verhindert werden soll
     * Predicate gibt true zurück -> Action wird verhindert, Predicate gibt false zurück -> Action wird nicht verhindert
     * @param predicate
     */
    registerPreventUserActionsException(predicate: LoaderUserActionPreventionPredicate): void {
        this.userActionsPreventionExceptions.push(predicate);
    }

    private async offAsync($element: JQuery, delay?: number): Promise<boolean> {
        this.removePendingElement($element[0]);

        const loaderId = $element.data(this.LOADER_DATA_ID);
        const count = this.decreaseCounter(loaderId);
        if (count !== 0)
            return false;

        const shouldHide = await this.shouldHideAsync(loaderId, delay);
        if (!shouldHide)
            return false;

        $element.removeClass(this.LOADER_CLASS);
        $element.removeAttr(this.LOADER_DATA_ID_ATTR);
        this.removeLoaderElements($element, loaderId);

        if ($element.parent().is('html'))
            this.removeUserActionPrevention();

        return true;
    }
    
    private isBodyElement($element: JQuery) {
        return $element.prop('tagName') === 'BODY';
    }

    private removePendingElement(element: HTMLElement) {
        const index = this.pendingLoaderElements.indexOf(element);
        if (index >= 0) {
            this.pendingLoaderElements.splice(index, 1);
        }
    }
    
    private removeLoaderElements($parent: JQuery, loaderId: number) {
        const selector = ['overlay', 'loader']
            .map(c => loaderId == undefined ? `>.${c}` : `.${c}[${this.LOADER_DATA_ID_ATTR}=${loaderId}]`)
            .join(',');
        const $elements = $parent.find(selector);

        $elements.remove();
    }

    private getId($element: JQuery): number | undefined {
        if (this.isBodyElement($element))
            return this.globalLoaderId;

        return $element.data(this.LOADER_DATA_ID);
    }

    private generateId() {
        let i = 0;

        while (i in this.counter) {
            i++;
        }

        return i;
    }

    private async shouldHideAsync(loaderId: number, delay?: number): Promise<boolean> {
        if (delay <= 0)
            return Promise.resolve(this.shouldHide(loaderId));

        return new Promise<boolean>(resolve => {
            this.hideTimers[loaderId] = window.setTimeout(() => {
                resolve(this.shouldHide(loaderId));
            }, delay ?? DEFAULT_LOADER_HIDE_DELAY_MS);
        });
    }

    private shouldHide(loaderId: number) {
        return this.getCount(loaderId) <= 0;
    }

    private resetTimer(loaderId: number) {
        const timerId = this.hideTimers[loaderId];
        if (timerId) {
            window.clearTimeout(timerId);
            delete this.hideTimers[loaderId];
        }
    }

    private increaseCounter(loaderId: number | undefined) {
        if (loaderId === undefined) {
            return 0;
        }

        if (!(loaderId in this.counter)) {
            this.counter[loaderId] = 1;
            return this.counter[loaderId];
        }

        if (this.counter[loaderId] <= 0) {
            this.counter[loaderId] = 1;
            return this.counter[loaderId];
        }

        return ++this.counter[loaderId];
    }

    private decreaseCounter(loaderId: number | undefined) {
        if (loaderId === undefined) {
            return 0;
        }

        if (!(loaderId in this.counter)) {
            this.counter[loaderId] = 0;
            return this.counter[loaderId];
        }

        if (this.counter[loaderId] <= 0) {
            this.counter[loaderId] = 0;
            return this.counter[loaderId];
        }
        return --this.counter[loaderId];
    }

    private getCount(id: number | undefined) {
        if (id === undefined) {
            return 0;
        }

        return this.counter[id];
    }

    while(element: JQuery | null, callback: Function) {
        this.show(element);
        callback();
        this.hide(element);
    }

    show($element?: JQuery, showLoader?: boolean, showOverlay?: boolean) {
        $element = $element || $('body');

        this.on($element, showLoader, showOverlay);
    }

    hideAsync(element?: JQuery, delay?: number): Promise<boolean> {
        element = element || $('body');

        return this.offAsync(element, delay);
    }

    hide(element?: JQuery, delay?: number) {
        this.hideAsync(element, delay);
    }

    removeAll() {
        window.setTimeout(() => {
            $('.overlay, .loader').remove();
        }, DEFAULT_LOADER_HIDE_DELAY_MS);
    }
}
